// =============================================================================
// T-404 — « Voir le mail d'origine » : le mail COMPLET d'une carte du Bureau (de, à, date, objet, corps entier, pièces jointes).
// Lecture seule : aucun envoi, aucune écriture. HTML assaini par le serveur PUIS reconstruit ici (jamais de script) ; sinon texte brut.
// Sur téléphone (≤ 639 px) : panneau plein écran avec un bouton « Fermer ». Mail introuvable : on le dit en clair, avec ce que la carte sait.
// Charte : jetons var(--oh-*) uniquement (2 modes).
// =============================================================================
import { useEffect, useMemo, useState } from "react";
import { Download, MailOpen, Paperclip, X } from "lucide-react";

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../../components/ui/dialog";
import { OhStatusNote } from "../../../components/ui/oh-charte";
import Bouton, { BOUTON } from "./boutonBureau";
import { neutraliserHtml } from "./mailHtmlSur";
import * as api from "./bureauAmoApi";
import { agentApi } from "./bureauAmoApi";

const muted = { color: "var(--oh-text-muted)", fontSize: 14, lineHeight: 1.6 };
const ZONE = { margin: 0, fontFamily: "inherit", fontSize: 14, lineHeight: 1.6, color: "var(--oh-text)", background: "var(--oh-bg-surface)", border: "0.5px solid var(--oh-border)",
  borderRadius: "var(--oh-radius-control)", padding: "var(--oh-space-3)", overflow: "auto", overflowWrap: "anywhere" };
// Plein écran sans unité de hauteur de fenêtre (la barre d'adresse mobile la fausse) : les quatre bords collés à la fenêtre.
const PLEIN_ECRAN = { top: 0, left: 0, right: 0, bottom: 0, transform: "none", width: "auto", height: "auto", alignContent: "start", maxWidth: "none", maxHeight: "none", borderRadius: 0, overflowY: "auto" };

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? String(iso).slice(0, 16) : d.toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" });
};
const fmtTaille = (n) => (n < 1024 ? `${n} o` : n < 1048576 ? `${Math.round(n / 1024)} Ko` : `${(n / 1048576).toFixed(1)} Mo`);
const personne = (p) => (p && (p.nom ? `${p.nom} <${p.adresse}>` : p.adresse)) || "—";
const personnes = (l) => (l && l.length ? l.map(personne).join(", ") : "—");

function surTelephone() {
  try { return typeof window !== "undefined" && typeof window.matchMedia === "function" && window.matchMedia("(max-width: 639px)").matches; } catch { return false; }
}

function Ligne({ nom, valeur, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      <dt style={{ ...muted, minWidth: 64 }}>{nom}</dt>
      <dd style={{ margin: 0, color: "var(--oh-text)", overflowWrap: "anywhere" }} data-testid={testid}>{valeur}</dd>
    </div>
  );
}

/** Contenu du panneau (sans la fenêtre) : sert aussi aux tests. */
export function MailOrigineContenu({ refCarte, onRecharger, etat, urlPiece = api.urlPieceCarte }) {
  const d = etat.donnees;
  const complet = Boolean(d && d.trouve);
  const html = useMemo(() => (complet && d.html ? neutraliserHtml(d.html) : ""), [complet, d]);
  if (etat.chargement) return <p style={muted} data-testid="mail-origine-chargement">Lecture du mail d'origine…</p>;
  if (etat.erreur) {
    const introuvable = etat.erreur.status === 404 || etat.erreur.status === 422;   // T-404e : un refus définitif ne se rejoue pas
    return (
      <OhStatusNote variant={introuvable ? "info" : "error"} data-testid={introuvable ? "mail-origine-introuvable" : "mail-origine-erreur"}
        title={introuvable ? "Mail d'origine introuvable" : "Le mail d'origine n'a pas pu être chargé"}>
        <div className="space-y-2">
          <p style={{ margin: 0 }}>{introuvable ? "Cette carte ne renvoie à aucun mail que le Bureau peut retrouver." : "Réessayez dans un instant."}</p>
          {!introuvable && <Bouton data-testid="mail-origine-recharger" onClick={onRecharger}>Réessayer</Bouton>}
        </div>
      </OhStatusNote>
    );
  }
  if (!d) return null;
  const texte = String(d.texte || "");
  return (
    <div className="space-y-3" data-testid="mail-origine">
      {!complet && (
        <OhStatusNote variant={d.raison === "imap_indisponible" ? "error" : "info"} data-testid="mail-origine-introuvable" title={d.mention || "Mail d'origine introuvable"}>
          <div className="space-y-2">
            <p style={{ margin: 0 }}>{texte.trim() ? "Voici le seul extrait que le Bureau a gardé de ce mail." : "Le Bureau n'a gardé aucun extrait de ce mail."}</p>
            {d.raison === "imap_indisponible" && <Bouton data-testid="mail-origine-recharger" onClick={onRecharger}>Réessayer</Bouton>}
          </div>
        </OhStatusNote>
      )}
      <p style={{ margin: 0, fontWeight: 500, fontSize: 16, color: "var(--oh-text)", overflowWrap: "anywhere" }} data-testid="mail-origine-objet">{d.objet || "(sans objet)"}</p>
      <dl className="space-y-1" style={{ margin: 0, fontSize: 14 }} data-testid="mail-origine-entetes">
        <Ligne nom="De" valeur={personne(d.expediteur)} testid="mail-origine-de" />
        <Ligne nom="À" valeur={complet ? personnes(d.destinataires) : "—"} testid="mail-origine-a" />
        {complet && d.copie?.length > 0 && <Ligne nom="Copie" valeur={personnes(d.copie)} testid="mail-origine-cc" />}
        <Ligne nom="Date" valeur={fmtDate(d.date)} testid="mail-origine-date" />
      </dl>
      {html ? (
        <div data-testid="mail-origine-html" style={ZONE} dangerouslySetInnerHTML={{ __html: html }} />
      ) : texte.trim() ? (
        <pre data-testid="mail-origine-texte" style={{ ...ZONE, whiteSpace: "pre-wrap" }}>{texte}</pre>
      ) : (
        <p style={muted} data-testid="mail-origine-vide">Ce mail ne contient pas de texte.</p>
      )}
      {complet && d.images_masquees > 0 && (
        <p style={muted}>{d.images_masquees} image{d.images_masquees > 1 ? "s" : ""} du mail non affichée{d.images_masquees > 1 ? "s" : ""} (elles signaleraient votre lecture à l'expéditeur).</p>
      )}
      {complet && d.pieces?.length > 0 && (
        <div className="space-y-1" data-testid="mail-origine-pieces">
          <p style={{ margin: 0, fontWeight: 500, color: "var(--oh-text)" }}><Paperclip className="w-4 h-4 inline mr-1" aria-hidden="true" />Pièces jointes ({d.pieces.length})</p>
          <ul style={{ listStyle: "none", padding: 0, margin: 0 }} className="space-y-1">
            {d.pieces.map((p) => (
              <li key={p.index} className="flex flex-wrap items-center justify-between gap-2" style={{ fontSize: 14, color: "var(--oh-text)" }}>
                <span style={{ overflowWrap: "anywhere" }}>{p.nom} <span style={muted}>· {fmtTaille(p.taille)}</span></span>
                <a href={urlPiece(refCarte, p.index)} download data-testid={`mail-origine-piece-${p.index}`} aria-label={`Télécharger la pièce jointe ${p.nom}`} rel="noopener"
                  style={{ ...BOUTON.base, ...BOUTON.discret, display: "inline-flex", alignItems: "center", textDecoration: "none", minHeight: 44 }}>
                  <Download className="w-4 h-4 inline mr-1" aria-hidden="true" />Télécharger
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/** Fenêtre « Mail d'origine » d'une carte. Fermée si `ouvert` est faux ; ouverte sans `refCarte` : « Mail d'origine introuvable ». */
export default function MailOriginePanneau({ refCarte, ouvert = Boolean(refCarte), onFerme, agent = "matthieu" }) {
  const lien = agent === "matthieu" ? api : agentApi(agent);   // T-406 : un agent ne lit que les mails qu'on lui a transmis (le serveur le vérifie)
  const [etat, setEtat] = useState({ chargement: true, erreur: null, donnees: null });
  const [recharge, setRecharge] = useState(0);
  useEffect(() => {
    if (!ouvert) return undefined;
    if (!refCarte) { setEtat({ chargement: false, erreur: { status: 404 }, donnees: null }); return undefined; }   // aucune référence : on le dit en clair
    let actif = true;
    setEtat({ chargement: true, erreur: null, donnees: null });
    lien.mailOrigineCarte(refCarte).then(
      (d) => { if (actif) setEtat({ chargement: false, erreur: null, donnees: d }); },
      (e) => { if (actif) setEtat({ chargement: false, erreur: e, donnees: null }); },
    );
    return () => { actif = false; };
  }, [refCarte, ouvert, recharge, agent]);   // eslint-disable-line react-hooks/exhaustive-deps
  const phone = surTelephone();
  return (
    <Dialog open={ouvert} onOpenChange={(o) => { if (!o) onFerme(); }}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" style={phone ? PLEIN_ECRAN : undefined} data-testid="mail-origine-panneau" data-plein-ecran={phone ? "oui" : "non"}>
        <DialogHeader style={{ position: "sticky", top: 0, zIndex: 1, background: "inherit", paddingBottom: "var(--oh-space-2)" }}>
          <div className="flex flex-wrap items-center justify-between gap-2" style={{ paddingRight: 32 }}>
            <DialogTitle><MailOpen className="w-5 h-5 inline mr-2" aria-hidden="true" />Mail d'origine</DialogTitle>
            <Bouton type="secondaire" data-testid="mail-origine-fermer" style={{ height: 44 }} onClick={onFerme}><X className="w-4 h-4 inline mr-1" aria-hidden="true" />Fermer</Bouton>
          </div>
          <DialogDescription className="sr-only">Le mail reçu, en lecture seule. Rien n'est envoyé d'ici.</DialogDescription>
        </DialogHeader>
        <MailOrigineContenu refCarte={refCarte} etat={etat} urlPiece={lien.urlPieceCarte} onRecharger={() => setRecharge((n) => n + 1)} />
      </DialogContent>
    </Dialog>
  );
}

/** Bouton visible « Voir le mail d'origine » : ouvre le panneau de la carte `refCarte`. */
export function BoutonMailOrigine({ refCarte, testid = "bureau-simple-mail-origine", onVoir }) {
  return (
    <Bouton type="secondaire" data-testid={testid} aria-label="Voir le mail d'origine" style={{ minHeight: 44 }}
      onClick={(e) => { e.stopPropagation(); onVoir(refCarte); }}>
      <MailOpen className="w-4 h-4 inline mr-1" aria-hidden="true" />Voir le mail d'origine
    </Bouton>
  );
}
